import { ref, shallowRef } from 'vue'
import type { Channel, Event, LocalMessage, StreamChat as StreamChatClient, TokenOrProvider } from 'stream-chat'

import { CONVERSATIONS_PAGE_SIZE, MESSAGING_CHANNEL_TYPE, REQUEST_TIMEOUT_MS } from '../consts'
import { IN_TOUCH_CHAT_MESSAGE_SEND_OPTIONS } from '../utils/configure-in-touch-chat-channel'
import { loadStreamChatSdk } from '../utils/load-stream-chat-sdk'
import type { InTouchChatClientApi, InTouchChatConnectOptions, QueryUserChannelsOptions } from './types'

export type { InTouchChatConnectOptions } from './types'

const TOKEN_EXPIRED_ERROR_CODE = 40
// Translations will be added in InTouchChat V2
const TOKEN_REQUIRED_MESSAGE = 'Stream Chat connect requires tokenOrProvider (or token)'
// Translations will be added in InTouchChat V2
const CONNECT_ERROR_MESSAGE = 'Failed to connect'
// Translations will be added in InTouchChat V2
const NOT_CONNECTED_MESSAGE = 'Not connected'
// Translations will be added in InTouchChat V2
const WATCH_CHANNEL_ERROR_MESSAGE = 'Failed to watch channel'
// Translations will be added in InTouchChat V2
const SEND_MESSAGE_ERROR_MESSAGE = 'Failed to send message'

function isTokenExpiredError (err: unknown): boolean {
  return (err as { code?: number } | undefined)?.code === TOKEN_EXPIRED_ERROR_CODE
}

/**
 * Stream Chat client composable.
 *
 * Lifecycle follows Stream's JS / stream-chat-react guidance:
 * - one long-lived client (`StreamChat.getInstance`)
 * - `connectUser` once per session with a token provider for refresh
 * - `disconnectUser` only via explicit `disconnect()` (e.g. logout) — not on consumer unmount
 *
 * Apps should hold a single shared instance (module singleton / provide-inject),
 * analogous to calling `useCreateChatClient` once at the React app root.
 */
export function useInTouchChatClient (): InTouchChatClientApi {
  const client = shallowRef<StreamChatClient | null>(null)
  const channel = shallowRef<Channel | null>(null)
  const messages = ref<LocalMessage[]>([])
  const connected = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const streamUserId = ref<string | null>(null)

  let messageListener: { unsubscribe: () => void } | null = null
  let currentTokenOrProvider: TokenOrProvider | null = null
  let connectInFlight: Promise<void> | null = null
  let refreshInFlight: Promise<void> | null = null

  const hasActiveUser = (): boolean => Boolean(client.value?.userID)

  const resolveTokenOrProvider = (options: InTouchChatConnectOptions): TokenOrProvider => {
    const tokenOrProvider = options.tokenOrProvider ?? options.token

    if (!tokenOrProvider) {
      throw new Error(TOKEN_REQUIRED_MESSAGE)
    }

    return tokenOrProvider
  }

  const refreshAuthToken = async (): Promise<void> => {
    if (refreshInFlight) {
      return refreshInFlight
    }

    const activeClient = client.value
    const activeStreamUserId = streamUserId.value

    if (!activeClient || !activeStreamUserId || !currentTokenOrProvider) {
      return
    }

    refreshInFlight = (async () => {
      await activeClient.connectUser({ id: activeStreamUserId }, currentTokenOrProvider)
      connected.value = true
    })().finally(() => {
      refreshInFlight = null
    })

    return refreshInFlight
  }

  const connect = async (options: InTouchChatConnectOptions): Promise<void> => {
    const tokenOrProvider = resolveTokenOrProvider(options)

    if (connectInFlight) {
      await connectInFlight

      if (connected.value && hasActiveUser() && streamUserId.value === options.streamUserId) {
        currentTokenOrProvider = tokenOrProvider
        return
      }
    }

    if (connected.value && hasActiveUser() && streamUserId.value === options.streamUserId) {
      currentTokenOrProvider = tokenOrProvider
      return
    }

    loading.value = true
    error.value = null

    connectInFlight = (async () => {
      const StreamChat = await loadStreamChatSdk()
      const chatClient = StreamChat.getInstance(options.apiKey, {
        timeout: REQUEST_TIMEOUT_MS,
      })

      if (chatClient.userID && chatClient.userID !== options.streamUserId) {
        await chatClient.disconnectUser()
      }

      if (!chatClient.userID) {
        // Pass token provider (not a static string) so Stream refreshes on expiry /
        // auth errors / reconnect — see Tokens & Authentication docs.
        // Second arg is token XOR provider; our API returns expires_in so use provider.
        await chatClient.connectUser({ id: options.streamUserId }, tokenOrProvider)
      }

      client.value = chatClient
      streamUserId.value = options.streamUserId
      currentTokenOrProvider = tokenOrProvider
      connected.value = true
    })()

    try {
      await connectInFlight
    } catch (error_) {
      error.value = error_ instanceof Error ? error_.message : CONNECT_ERROR_MESSAGE
      connected.value = false
      throw error_
    } finally {
      connectInFlight = null
      loading.value = false
    }
  }

  const queryUserChannels = async (options: QueryUserChannelsOptions = {}): Promise<Channel[]> => {
    const activeClient = client.value
    const activeStreamUserId = streamUserId.value

    if (!activeClient || !activeStreamUserId || !hasActiveUser()) {
      throw new Error(NOT_CONNECTED_MESSAGE)
    }

    const runQuery = () =>
      activeClient.queryChannels(
        {
          type: MESSAGING_CHANNEL_TYPE,
          members: { $in: [activeStreamUserId] },
        },
        [{ last_message_at: -1 }],
        {
          watch: true,
          state: true,
          limit: CONVERSATIONS_PAGE_SIZE,
          offset: options.offset ?? 0,
          // List only needs the last message preview; default message_limit is 25.
          message_limit: 1,
          // member_limit omitted — Stream default (server-driven / 100) is enough for 1:1 chats.
          // @see https://getstream.io/chat/docs/javascript/query_channels/
        }
      )

    try {
      return await runQuery()
    } catch (error_) {
      if (isTokenExpiredError(error_)) {
        await refreshAuthToken()
        return await runQuery()
      }

      throw error_
    }
  }

  const onClientEvent = (handler: (event: Event) => void): { unsubscribe: () => void } | null => {
    if (!client.value) {
      return null
    }
    return client.value.on(handler)
  }

  const watchChannel = async (channelType: string, channelId: string): Promise<Channel> => {
    if (!client.value || !hasActiveUser()) {
      throw new Error(NOT_CONNECTED_MESSAGE)
    }

    // Reuse the already-watched channel — avoids a redundant Stream round-trip on route sync.
    if (channel.value?.id === channelId && channel.value.type === channelType) {
      return channel.value
    }

    loading.value = true
    error.value = null

    try {
      messageListener?.unsubscribe()
      messageListener = null

      const chatChannel = client.value.channel(channelType, channelId)

      try {
        await chatChannel.watch()
      } catch (error_) {
        if (isTokenExpiredError(error_)) {
          await refreshAuthToken()
          await chatChannel.watch()
        } else {
          throw error_
        }
      }

      channel.value = chatChannel
      messages.value = [...chatChannel.state.messages]

      messageListener = chatChannel.on('message.new', () => {
        messages.value = [...chatChannel.state.messages]
      })

      return chatChannel
    } catch (error_) {
      error.value = error_ instanceof Error ? error_.message : WATCH_CHANNEL_ERROR_MESSAGE
      throw error_
    } finally {
      loading.value = false
    }
  }

  const sendMessage = async (text: string): Promise<void> => {
    if (!channel.value || !text.trim()) {
      return
    }

    error.value = null

    try {
      await channel.value.sendMessage({ text: text.trim() }, IN_TOUCH_CHAT_MESSAGE_SEND_OPTIONS)
      messages.value = [...channel.value.state.messages]
    } catch (error_) {
      if (isTokenExpiredError(error_) && channel.value) {
        await refreshAuthToken()
        await channel.value.sendMessage({ text: text.trim() }, IN_TOUCH_CHAT_MESSAGE_SEND_OPTIONS)
        messages.value = [...channel.value.state.messages]
        return
      }

      error.value = error_ instanceof Error ? error_.message : SEND_MESSAGE_ERROR_MESSAGE
      throw error_
    }
  }

  const disconnect = async (): Promise<void> => {
    currentTokenOrProvider = null
    refreshInFlight = null
    messageListener?.unsubscribe()
    messageListener = null

    if (client.value) {
      await client.value.disconnectUser()
    }

    client.value = null
    channel.value = null
    messages.value = []
    streamUserId.value = null
    connected.value = false
  }

  /** Clear the active channel without disconnecting the shared Stream client. */
  const clearChannel = (): void => {
    messageListener?.unsubscribe()
    messageListener = null
    channel.value = null
    messages.value = []
  }

  return {
    client,
    channel,
    messages,
    connected,
    loading,
    error,
    streamUserId,
    connect,
    queryUserChannels,
    onClientEvent,
    watchChannel,
    sendMessage,
    clearChannel,
    disconnect,
  }
}
